import React, { useMemo, useState } from 'react';
import AdminRepository from '../../services/AdminRepository.js';
import { normalizeLicenseKey } from '../../utils/licenseKeyUtils.js';
import {
  RegisteredLicenseKeyRow,
  EmptyLicenseSlotRow,
  LicenseKeyDeleteModal
} from './LicenseKeyEditor.jsx';
import './LicenseKeyEditor.css';

/**
 * ライセンス管理画面 — license-list スロットエディタ
 */
const RegistryLicenseKeyEditor = ({ slots, onRefresh }) => {
  const adminRepo = AdminRepository.getInstance();
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [actionError, setActionError] = useState('');

  const registeredKeys = useMemo(() => {
    return slots
      .filter((s) => s.isRegistered)
      .map((s) => normalizeLicenseKey(s.name));
  }, [slots]);

  const runAction = async (fn) => {
    setBusy(true);
    setActionError('');
    try {
      await fn();
      await onRefresh();
    } catch (err) {
      setActionError(err.message || '操作に失敗しました');
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const handleRename = async (oldKey, newKey) => {
    await runAction(() => adminRepo.renameLicenseRegistryKey(oldKey, newKey));
  };

  const handleAdd = async (key) => {
    await runAction(() => adminRepo.addLicenseRegistryKey(key));
  };

  const handleReorder = async (keys) => {
    await runAction(() => adminRepo.reorderLicenseRegistryKeys(keys));
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    setDeleteError('');
    try {
      await adminRepo.removeLicenseRegistryKey(deleteTarget, true);
      setDeleteTarget(null);
      await onRefresh();
    } catch (err) {
      setDeleteError(err.message || '削除に失敗しました');
    } finally {
      setBusy(false);
    }
  };

  const moveKeyAt = async (idx, direction) => {
    const target = direction === 'up' ? idx - 1 : idx + 1;
    if (idx < 0 || target < 0 || target >= registeredKeys.length) return;
    const next = [...registeredKeys];
    next[idx] = registeredKeys[target];
    next[target] = registeredKeys[idx];
    await handleReorder(next);
  };

  return (
    <div className="license-key-editor">
      <p className="license-keys-section-desc">
        登録済みキーの編集は<strong>改名</strong>として全製品・全クリニック・招待特典・Stripe に反映されます。
        新規登録は空き枠の＋ボタンから行います。削除すると後ろのキーが前に詰まります。
      </p>

      {actionError && <p className="license-key-action-error">{actionError}</p>}

      <div className="license-key-list">
        {(() => {
          let registeredIndex = 0;
          return slots.map((slot) => {
            if (slot.isReserved) {
              return (
                <div key={slot.key} className="license-key-row license-key-row--reserved">
                  <span className="license-key-slot-label">{slot.key}</span>
                  <span className="license-key-reserved-value">{slot.name}</span>
                  <span className="license-key-reserved-note">（システム予約）</span>
                </div>
              );
            }

            if (slot.isRegistered) {
              const key = normalizeLicenseKey(slot.name);
              const regIndex = registeredIndex;
              registeredIndex += 1;
              return (
                <RegisteredLicenseKeyRow
                  key={`${slot.key}-${key}`}
                  slotLabel={slot.key}
                  value={slot.name}
                  originalKey={key}
                  normalizedPreview={normalizeLicenseKey(slot.name)}
                  isPrimary={regIndex === 0}
                  canMoveUp={regIndex > 0}
                  canMoveDown={regIndex < registeredKeys.length - 1}
                  onRename={handleRename}
                  onDelete={(k) => { setDeleteError(''); setDeleteTarget(k); }}
                  onMoveUp={() => moveKeyAt(regIndex, 'up')}
                  onMoveDown={() => moveKeyAt(regIndex, 'down')}
                  disabled={busy}
                />
              );
            }

            const placeholderText = slot.isPlaceholder && slot.name
              ? slot.name
              : '未登録';

            return (
              <EmptyLicenseSlotRow
                key={slot.key}
                slotLabel={slot.key}
                placeholderText={placeholderText}
                onAdd={handleAdd}
                disabled={busy}
              />
            );
          });
        })()}
      </div>

      <LicenseKeyDeleteModal
        open={!!deleteTarget}
        keyName={deleteTarget}
        onCancel={() => { setDeleteTarget(null); setDeleteError(''); }}
        onConfirm={handleDeleteConfirm}
        loading={busy}
        error={deleteError}
      />
    </div>
  );
};

export default RegistryLicenseKeyEditor;
